import { useEffect } from "react";
import { Link } from "react-router";
import { useMe, useOnboarding, useStarterTeam } from "../api.ts";
import { STARTER_TEAM_SEATS, clearStarterTeamFlag, connectSteps, isLoopbackOrigin, machineRunsAgents, onboardingView, readStarterTeamFlag, type OnboardingStatus } from "../lib/onboarding.ts";
import { Card } from "../calm.tsx";
import { Button, ErrorNote, Markdown } from "../ui.tsx";

/** How often the connect steps re-read the status, so a machine that comes online clears them without a reload. */
const CONNECT_POLL_MS = 15_000;

/**
 * First run (task #1525): on an office with no agents, offer the starter team in one click; after this browser
 * created it, show how to connect a machine to host the agents. Renders nothing otherwise.
 */
export function StarterTeamCard() {
  const admin = useMe().data?.access === "admin";
  // Read on each render: the creation sets the flag before the status it causes is re-rendered.
  const startedHere = readStarterTeamFlag();
  const status = useOnboarding(startedHere && admin ? CONNECT_POLL_MS : false).data ?? null;
  const create = useStarterTeam();
  // A machine is running the agents: the connect steps are done, so the browser forgets the creation.
  const machineUp = machineRunsAgents(status);
  useEffect(() => {
    if (startedHere && machineUp) clearStarterTeamFlag();
  }, [startedHere, machineUp]);
  const view = onboardingView(status, { admin, startedHere });
  if (view === "offer") return <StarterTeamOffer create={create} />;
  if (view === "connect" && status?.connect) return <ConnectMachine connect={status.connect} created={create.data?.agents} />;
  return null;
}

function StarterTeamOffer({ create }: { create: ReturnType<typeof useStarterTeam> }) {
  return (
    <section className="mt-8" data-testid="starter-team-offer">
      <Card>
        <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">Your office has no agents yet</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
          An idea needs agents to write its spec and build it. Create the starter team in one click. The agents are owned by you: rename them, change their areas or archive them later.
        </p>
        <ul className="mt-4 space-y-1.5 text-[14px] text-ink-2" data-testid="starter-team-seats">
          {STARTER_TEAM_SEATS.map((seat) => (
            <li key={seat.name}>
              <span className="font-mono text-ink">{seat.name}</span> · {seat.role}
            </li>
          ))}
        </ul>
        <div className="mt-5">
          <Button variant="primary" data-testid="starter-team-create" disabled={create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create the starter team"}
          </Button>
        </div>
        <div className="mt-3">
          <ErrorNote error={create.error} />
        </div>
      </Card>
    </section>
  );
}

function ConnectMachine({ connect, created }: { connect: NonNullable<OnboardingStatus["connect"]>; created?: { name: string }[] }) {
  const origin = window.location.origin;
  const steps = connectSteps(connect, origin);
  return (
    <section className="mt-8" data-testid="connect-machine">
      <Card>
        <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">Connect a machine to run the agents</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
          {created?.length ? `Created ${created.map((a) => a.name).join(", ")}. ` : null}
          The agents run on a machine with Claude Code installed and logged in. They take no work until a machine is running them.
        </p>
        {isLoopbackOrigin(origin) && (
          <p className="mt-3 text-[13px] text-high" data-testid="connect-loopback-warning">
            This page is on {new URL(origin).hostname}, which other machines cannot reach. Replace it in the commands with the hub's network address.
          </p>
        )}
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-[14px] leading-relaxed text-ink-2" data-testid="connect-machine-steps">
          {steps.map((step) => (
            <li key={step}>
              <Markdown text={step} />
            </li>
          ))}
        </ol>
        <p className="mt-4 text-[13px] text-ink-3">
          <Link to="/connect" className="underline hover:text-ink">Setup guide</Link>
          {" · "}
          <Link to="/team" className="underline hover:text-ink">Agent tokens</Link>
        </p>
      </Card>
    </section>
  );
}
